import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Box, CircularProgress } from '@mui/material';
import OnboardingReminder from './OnboardingReminder';
import SubscriptionPaywall, { EF_PRODUCTS } from './billing/SubscriptionPaywall';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** If true, requires authentication. If false, shows onboarding reminder instead of blocking */
  requireAuth?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAuth = true }) => {
  const { isAuthenticated, loading, firebaseUser, getIdToken } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Check for Executive Force AI SSO token or onboarding completion
  const hasOnboardingComplete = localStorage.getItem('onboarding_completed') === 'true';
  const hasSSOToken = localStorage.getItem('access_token')?.startsWith('sso_');
  const hasAccessToken = !!localStorage.getItem('access_token');
  const hasAscinToken = !!localStorage.getItem('ascin_token');

  // If strict auth is required and user isn't authenticated by ANY method
  if (requireAuth && !isAuthenticated && !hasAccessToken && !hasSSOToken && !hasAscinToken) {
    // ASCIN-native flow: prefer the ASCIN login page over the EF SSO landing
    return <Navigate to="/ascin-login" state={{ from: location }} replace />;
  }

  // For Executive Force AI integration: allow access with SSO token or access token
  // Show onboarding reminder if onboarding isn't complete (but don't block access)
  const showOnboardingReminder = !hasOnboardingComplete && (hasAccessToken || hasSSOToken || isAuthenticated || hasAscinToken);

  // Wrap in subscription paywall if user is authenticated
  const content = (
    <>
      {showOnboardingReminder && <OnboardingReminder />}
      {children}
    </>
  );

  // Only enforce paywall for Firebase-authenticated users (paywall needs a Firebase ID token).
  // ASCIN-native tenants are billed separately and skip this gate.
  if ((isAuthenticated || hasAccessToken || hasSSOToken) && !hasAscinToken) {
    return (
      <SubscriptionPaywall
        product={EF_PRODUCTS['ef-link']}
        getAuthToken={async () => {
          return await getIdToken();
        }}
        userId={firebaseUser?.uid}
      >
        {content}
      </SubscriptionPaywall>
    );
  }

  return content;
};

export default ProtectedRoute;
